<script setup lang="ts">
import { computed, ref } from 'vue'
import { usePageStore } from '@voix/store/pageStore'
import { useCookie, useRouter } from '#imports'

const showAlert = ref(true)
const pageStore = usePageStore()

// Releases
const releasePreviewCookie = useCookie('voix-release-preview')
const isViewingRelease = computed(() => releasePreviewCookie.value)

const showReleaseNotification = useCookie('voix-show-release-notification')
if (showReleaseNotification.value === false)
  showAlert.value = false

function viewActiveRelease() {
  releasePreviewCookie.value = null
  window.location.reload()
}

const releaseName = computed(() => {
  return pageStore.currentPage?.current_release?.label ? `: ${pageStore.currentPage?.current_release?.label}` : ''
})

function toggleAlert() {
  showAlert.value = !showAlert.value
  showReleaseNotification.value = showAlert.value
}

const router = useRouter()
function goToReleases() {
  // Use the router to navigate to the releases page
  router.push({ name: 'voix-settings-releases-en' })
}
</script>

<template>
  <div>
    <div
      v-if="isViewingRelease" class="fixed top-0 left-[80px] right-0 duration-200 ease-out" :class="{
        '-translate-y-[90%]': !showAlert,
      }"
    >
      <div class="h-[50px] flex items-center justify-between voix-admin-bg-dark text-white text-sm font-medium">
        <div class="relative isolate w-full h-full">
          <div class="fixed pointer-events-none z-10 left-0 top-0 h-[50px] right-0 bg-orange-500" style="mask-image: linear-gradient(0.25turn, rgb(0 0 0 / 25%), transparent 50%);">
            <div class="flex flex-col -rotate-45 translate-y-[100px] translate-x-[-200px] origin-top-left">
              <template v-for="n in 10" :key="n">
                <div class="w-[200px] h-[30px] voix-admin-bg" :style="` transform: translateX(${60 * n}px);`" />
                <div class="w-[200px] h-[30px] voix-admin-bg-darkest" :style="` transform: translateX(${60 * n}px);`" />
              </template>
            </div>
          </div>
          <div class="absolute z-20 inset-0 bg-red-500 knight-rider" style="mask-image: linear-gradient(0.25turn, transparent, rgb(0 0 0 / 50%) 50%, transparent );" />
        </div>
        <div class="absolute top-0 left-0 bottom-0 flex items-center pl-6 py-2 pr-12">
          <span class="font-black">Note:</span>&nbsp;&nbsp; You're currently viewing release<span class="font-bold">{{ releaseName }}</span>
        </div>
        <div class="absolute right-0 top-0 bottom-0 flex space-x-2 py-2 pr-2">
          <NuxtLink :to="{ name: 'voix-settings-releases-en' }" class="voix-admin-bg-darkest voix-admin-text-lightest px-4 py-1.5 rounded text-sm" @click="goToReleases">
            Manage Releases
          </NuxtLink>
          <button class="voix-admin-bg-darkest voix-admin-text-lightest px-4 py-1.5 rounded text-sm focus:outline-none" @click="viewActiveRelease">
            View current release
          </button>
        </div>
      </div>

      <div
        class="absolute bottom-0 left-0 right-0 pointer-events-none flex  justify-center duration-500"
        :class="{
          'scale-100': showAlert,
          'translate-y-12 scale-75': !showAlert,
        }"
      >
        <button
          class="pointer-events-auto relative duration-200 scale-75 hover:scale-100" @click="toggleAlert"
        >
          <div
            class="duration-1000" :class="{
              'opacity-0': showAlert,
              'opacity-100': !showAlert,
            }"
          >
            <div class="absolute z-0 inset-0 blur">
              <Icon class="text-lg text-orange-500 w-12 h-12 -translate-x-[10px] -translate-y-[10px] slow-spin-reverse " name="heroicons:exclamation-triangle-20-solid" />
            </div>
            <div class="absolute z-0 inset-0 blur">
              <Icon class="text-lg text-teal-500/70 w-12 h-12 -translate-x-[10px] -translate-y-[10px] fast-spin " name="heroicons:exclamation-triangle-20-solid" />
            </div>
            <div class="absolute z-0 inset-0 blur">
              <Icon class="text-lg text-indigo-500 w-12 h-12 translate-x-[10px] translate-y-[10px] slow-spin " name="heroicons:exclamation-triangle-20-solid" />
            </div>
          </div>
          <div class="relative z-10 w-12 h-12 flex justify-center items-center">
            <Icon
              :class="{
                'opacity-0': showAlert,
                'opacity-100': !showAlert,
              }" class="absolute top-0 left-0 z-10 text-white text-lg w-12 h-12" name="heroicons:exclamation-triangle-20-solid"
            />
            <Icon
              :class="{
                'opacity-0': !showAlert,
                'opacity-100': showAlert,
              }" class="text-white text-lg w-8 h-8" name="heroicons:eye-slash-20-solid"
            />
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fast-spin{animation:spin 3s linear infinite}.slow-spin-reverse{animation:spin 5s infinite reverse}.slow-spin{animation:spin 5s linear infinite}@keyframes spin{0%{transform:rotate(0deg)}to{transform:rotate(1turn)}}.knight-rider{animation:scan 5s linear infinite}@keyframes scan{0%{transform:translateX(-100%)}to{transform:translateX(100%)}}
</style>
